import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { readDashboardCache, writeDashboardCache } from '../utils/dashboardCache';

const HOURS = 8;
const timeLabel = iso => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// Shows the club's current code of the day until it expires or someone
// replaces or clears it.
export default function CodeOfDayCard() {
  const { user, generateCodeOfTheDay, getCodeOfTheDay, clearCodeOfTheDay } = useAuth();
  const [code, setCode] = useState(() => readDashboardCache(user?.id).codeOfDay ?? null);
  const [busy, setBusy] = useState(false);

  const keep = next => {
    setCode(next);
    writeDashboardCache(user?.id, { codeOfDay: next });
  };

  useEffect(() => {
    getCodeOfTheDay().then(r => {
      if (r.success) keep(r.isActive ? { code: r.codeOfTheDay, expiresAt: r.expiresAt } : null);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide it the moment it expires
  useEffect(() => {
    if (!code) return undefined;
    const t = setTimeout(() => setCode(null), Math.max(0, new Date(code.expiresAt) - Date.now()));
    return () => clearTimeout(t);
  }, [code]);

  const generate = async () => {
    if (code && !window.confirm('Make a new code? The current one will stop working.')) return;
    setBusy(true);
    const r = await generateCodeOfTheDay(HOURS);
    if (r.success) keep({ code: r.codeOfTheDay, expiresAt: r.expiresAt });
    setBusy(false);
  };

  const clear = async () => {
    if (!window.confirm('Clear the code? Gymnasts won\'t be able to use it any more.')) return;
    setBusy(true);
    const r = await clearCodeOfTheDay();
    if (r.success) keep(null);
    setBusy(false);
  };

  return (
    <div className="card dashboard-code-of-day">
      <div className="card-header">
        <h3 className="card-title">Code of the Day</h3>
        <p className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
          Gymnasts in your club use this code to see their progress
        </p>
      </div>
      <div className="card-body">
        {code ? (
          <>
            <div className="dashboard-code-of-day__code" aria-label="Current code">{code.code}</div>
            <p className="dashboard-code-of-day__expiry">Works until {timeLabel(code.expiresAt)}</p>
            <div className="dashboard-code-of-day__actions">
              <button type="button" className="btn btn-outline" onClick={generate} disabled={busy}>New code</button>
              <button type="button" className="btn btn-secondary" onClick={clear} disabled={busy}>Clear</button>
            </div>
          </>
        ) : (
          <button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={generate} disabled={busy}>
            {busy ? 'Generating…' : 'Generate Code of the Day'}
          </button>
        )}
      </div>
    </div>
  );
}
